import { useEPGService } from '@/app/context/DIContext/DIServices'
import { EpgObject, ExtendedEpgElement } from '@/types/epg/Epg'
import { epgChannelRequest } from '@/types/epg/epgChannelRequest'
import Program from '@/shared/classes/epg/program/program'
import { checkIfProgramCurrentlyRuns } from '@/shared/classes/epg/helpers/isProgramRunning'
import {
    getFormattedProgramDuration,
    getProgramDurationProgress,
} from '@/shared/classes/epg/helpers/programDurationHelpers'
import { filterProgramsByTime } from '@/shared/hooks/EPG/lib'
import {
    CACHE_TRIM_AMOUNT,
    MILIS_24h,
    PROGRAMS_LENGTH_LIMIT,
} from '@/shared/constants'
import { IEPGServiceToya } from '@/shared/types/services/types/epgService/epgServiceInterfaces'
import { getPrograms } from '@/shared/classes/epg/helpers/getPrograms'
import { getErrorMessage } from '@/shared/lib'
import { ChannelType } from '@/shared/types/appTypes'
import { getWithAuth } from '@/api/httpRequests'
import { useEpgCacheStore } from '@/shared/store/epgCacheStore'
import { FetchEpgResponse, ProgramDataStreamIQ } from '@/types/epg/EpgStreamIq'
import { IEPGServiceStreamIQ } from '@/shared/types/services/types/epgService/IEPGServiceStreamIQ'
import { getBootstraps } from '@/clients/streamiq/shared/lib'

const useEPGStreamIQRepository = () => {
    const {
        createExtendedEpg,
        getProgramsForAllChannels,
        mapNewDataToProgram,
    } = useEPGService() as IEPGServiceStreamIQ

    const { direction } = useEpgCacheStore()

    const fetchEpgData = async (
        channelsParams: epgChannelRequest[]
    ): Promise<EpgObject[]> => {
        const bootstraps = getBootstraps()

        const res = await Promise.allSettled(
            channelsParams.map(async (item) => {
                let startTime
                let endTime

                const isDownOrFirstRender = ['down', 'firstRender'].includes(
                    direction || ''
                )

                if (item.end) {
                    const baseTime = item.end * 1000
                    startTime = isDownOrFirstRender
                        ? baseTime
                        : baseTime - MILIS_24h
                    endTime = isDownOrFirstRender
                        ? baseTime + MILIS_24h
                        : baseTime
                }

                return await getWithAuth(
                    `${bootstraps?.services?.['epg-service'].event}?startTime=${startTime}&endTime=${endTime}&channelId=${item.channelId}`,
                    false
                )
            })
        )

        const data: FetchEpgResponse[] = res
            .filter(
                (result): result is PromiseFulfilledResult<FetchEpgResponse> =>
                    result.status === 'fulfilled'
            )
            .map((result) => result.value)
            .filter((item) => Boolean(item))

        const programs = data.map((item) => item.response.resources).flat()

        if (!res) return []

        const mappedPrograms = programs
            .map((program: ProgramDataStreamIQ) => mapNewDataToProgram(program))
            .sort(
                (firstElement, secondElement) =>
                    firstElement.startMilis - secondElement.startMilis
            )

        const exdendedEPGObject: EpgObject[] = channelsParams.map((item) => ({
            channelId: item.channelId,
            programs: [
                ...mappedPrograms.filter(
                    (program: Program) => program.channelId === item.channelId
                ),
            ] as Program[],
        }))

        if (exdendedEPGObject) {
            return exdendedEPGObject
        }

        return []
    }

    const getUpdatedPrograms = (
        programs: Program[],
        newPrograms: Program[],
        direction: string | null,
        getCurrentProgramFunc: (programs: Program[]) => {
            currentProgram: Program | undefined
            programDurationProgress: number
            programFormattedDuration: string
        },
        milis24h: number,
        filterProgramsByTime: (
            programs: Program[],
            startTimeBoundary: number
        ) => Program[]
    ) => {
        switch (direction) {
            case 'up':
                return [...newPrograms, ...programs]
            case 'down':
                return [...programs, ...newPrograms]
            case 'firstRender': {
                const { currentProgram } = getCurrentProgramFunc([
                    ...programs,
                    ...newPrograms,
                ])
                return currentProgram
                    ? filterProgramsByTime(
                          newPrograms,
                          currentProgram.startMilis - milis24h
                      )
                    : newPrograms
            }
            default:
                return programs.sort(
                    (programA, programB) =>
                        programA.startMilis - programB.startMilis
                )
        }
    }

    const trimPrograms = (
        programs: Program[],
        direction: string | null,
        trimAmount: number
    ) => {
        if (direction === 'up') {
            const endTrimPoint = trimAmount
            return programs.slice(0, programs.length - endTrimPoint)
        } else {
            const startTrimPoint = trimAmount
            return programs.slice(startTrimPoint)
        }
    }

    const getCurrentProgram = (fullyNamesPrograms: Program[]) => {
        let currentProgram: Program | undefined
        let programDurationProgress = 0
        let programFormattedDuration = ''

        if (fullyNamesPrograms.length !== 0) {
            fullyNamesPrograms.forEach((fullyNamedProgram: Program) => {
                const { startMilis, endMilis, durationMilis } =
                    fullyNamedProgram
                if (checkIfProgramCurrentlyRuns(startMilis, endMilis) === 0) {
                    currentProgram = fullyNamedProgram
                    programDurationProgress = Math.floor(
                        getProgramDurationProgress(startMilis, durationMilis)
                    )
                    programFormattedDuration = getFormattedProgramDuration(
                        startMilis,
                        endMilis
                    )
                }
            })
        }
        return {
            currentProgram,
            programDurationProgress,
            programFormattedDuration,
        }
    }

    const updateEpgDataWithNewPrograms = (
        epgData: ExtendedEpgElement[],
        programs: EpgObject[],
        direction: string | null
    ): ExtendedEpgElement[] =>
        epgData.map((epgChannel) => {
            const update = programs
                .filter((item) => item.channelId === epgChannel.channelId)
                .flatMap((item) => item.programs)

            if (!update) return epgChannel

            let updatedPrograms = getUpdatedPrograms(
                epgChannel.programs,
                update,
                direction,
                getCurrentProgram,
                MILIS_24h,
                filterProgramsByTime
            )

            if (updatedPrograms.length > PROGRAMS_LENGTH_LIMIT) {
                updatedPrograms = trimPrograms(
                    updatedPrograms,
                    direction,
                    CACHE_TRIM_AMOUNT
                ).sort(
                    (programA, programB) =>
                        programA.startMilis - programB.startMilis
                )
            }

            return {
                ...epgChannel,
                programs: updatedPrograms,
            }
        })

    const mergeEpgArrays = (
        arr1: ExtendedEpgElement[],
        arr2: ExtendedEpgElement[]
    ): ExtendedEpgElement[] => {
        const mergedMap = new Map<string, ExtendedEpgElement>()

        const addOrMerge = (element: ExtendedEpgElement) => {
            const existingElement = mergedMap.get(element.channelId)

            if (existingElement) {
                const uniquePrograms = new Map<string, Program>()

                existingElement.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )

                element.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )

                existingElement.programs = Array.from(uniquePrograms.values())
            } else {
                const uniquePrograms = new Map<string, Program>()
                element.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )
                element.programs = Array.from(uniquePrograms.values())

                mergedMap.set(element.channelId, element)
            }
        }

        arr1.forEach(addOrMerge)
        arr2.forEach(addOrMerge)

        return Array.from(mergedMap.values())
    }

    const prepareEPGforSetToState = async (
        channels: ChannelType[],
        createExtendedEpg: IEPGServiceToya['createExtendedEpg'],
        filterWidevineOttEpg: IEPGServiceToya['filterWidevineOttEpg'],
        sortEpgElements: IEPGServiceToya['sortEpgElements']
    ) => {
        try {
            const channelsId = channels.map((channel) => channel.id || '')
            const apiEpg = await getPrograms(channelsId)
            const rawRemoteEpg = createExtendedEpg(channels, apiEpg)
            if (rawRemoteEpg) {
                const widevineOttEpg = filterWidevineOttEpg(
                    rawRemoteEpg,
                    channels
                )
                const sortedEpg = sortEpgElements(widevineOttEpg)
                return sortedEpg
            }
        } catch (error) {
            getErrorMessage('setEpg', error)
            throw error
        }
    }

    const createEPG = async (
        channelsList: ChannelType[],
        programs: EpgObject[]
    ): Promise<ExtendedEpgElement[] | null> =>
        new Promise((resolve, reject) => {
            try {
                const result = createExtendedEpg(channelsList, programs)
                resolve(result)
            } catch (error) {
                reject(error)
            }
        })

    return {
        mergeEpgArrays,
        fetchEpgData,
        getUpdatedPrograms,
        trimPrograms,
        getCurrentProgram,
        updateEpgDataWithNewPrograms,
        prepareEPGforSetToState,
        getProgramsForAllChannels,
        createEPG,
        createExtendedEpg,
    }
}

export { useEPGStreamIQRepository }
